import React, { useEffect, useMemo, useState } from 'react';
import ModalFrame from './modals/ModalFrame';
import SearchableSelect from './Form/SearchableSelect';
import { BATCH_STATUS_OPTIONS } from '../domain/enrollment/constants';
import { filterBatchesForAdditionalEnrollment } from '../domain/enrollment/batchEligibility';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';
import { enrollmentCommands } from '../domain/enrollment/enrollmentCommands';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

/**
 * Create an additional enrollment (SRP: UI + orchestration; rules in domain).
 */
const AddEnrollmentModal = ({
  isOpen,
  onClose,
  studentId,
  enrollments = [],
  batches = [],
  onSuccess,
  commands = enrollmentCommands
}) => {
  const [batchId, setBatchId] = useState('');
  const [enrollmentDate, setEnrollmentDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [batchStatus, setBatchStatus] = useState('Ongoing');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setBatchId('');
    setEnrollmentDate('');
    setRemarks('');
    setBatchStatus('Ongoing');
    setError('');
  }, [isOpen, studentId]);

  const eligibleBatches = useMemo(
    () => filterBatchesForAdditionalEnrollment(batches, enrollments),
    [batches, enrollments]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!studentId || !batchId) {
      setError('Student and batch are required.');
      return;
    }
    setSubmitting(true);
    try {
      await commands.createAdditionalEnrollment({
        studentId: String(studentId),
        batchId: parseInt(batchId, 10),
        enrollmentDate: enrollmentDate || undefined,
        remarks: remarks.trim() || undefined,
        batchStatus: batchStatus || 'Ongoing'
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const footer = studentId ? (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="add-enrollment-form"
        disabled={submitting || eligibleBatches.length === 0}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Saving…' : 'Add enrollment'}
      </button>
    </div>
  ) : null;

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      titleId="add-enrollment-title"
      title="Add enrollment"
      description="Enrolls this student in an additional batch without changing existing enrollments. The same student cannot be added twice to the same batch."
      footer={footer}
    >
      {!studentId ? (
        <p className="text-sm text-gray-700">No student selected.</p>
      ) : (
        <form id="add-enrollment-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
            <SearchableSelect
              value={batchId}
              onChange={(ev) => setBatchId(ev.target.value)}
              className="w-full"
              placeholder="Select batch…"
              required
              options={eligibleBatches}
              {...BATCH_SEARCHABLE_SELECT_PROPS}
            />
            {eligibleBatches.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                No batches available (student may already be enrolled in all listed batches).
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enrollment date (optional)
            </label>
            <input
              type="date"
              value={enrollmentDate}
              onChange={(ev) => setEnrollmentDate(ev.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status on new enrollment
            </label>
            <SearchableSelect
              value={batchStatus}
              onChange={(ev) => setBatchStatus(ev.target.value)}
              className="w-full"
              required
              options={BATCH_STATUS_OPTIONS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks (optional)
            </label>
            <textarea
              value={remarks}
              onChange={(ev) => setRemarks(ev.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
              {error}
            </div>
          )}
        </form>
      )}
    </ModalFrame>
  );
};

export default AddEnrollmentModal;
